'use client';

import { useEffect, useState } from 'react';
import { useTranslation } from 'next-i18next';
import { PulsatingBestieAvatar } from '@components/templates/audio-interview/partials/PulsatingBestieAvatar';
import { PhoneXMarkIcon } from '@heroicons/react/24/solid';
import type { VoiceStatus } from '@hooks/voice/useGeminiVoice';

interface GeminiVoiceCardProps {
  status: VoiceStatus;
  error: string | null;
  onClose: () => void;
}

export default function GeminiVoiceCard({ status, error, onClose }: GeminiVoiceCardProps) {
  const { t } = useTranslation('common');
  const [seconds, setSeconds] = useState(0);

  useEffect(() => {
    if (status !== 'connected') return undefined;
    setSeconds(0);
    const interval = setInterval(() => setSeconds((s) => s + 1), 1000);
    return () => clearInterval(interval);
  }, [status]);

  const mm = String(Math.floor(seconds / 60)).padStart(2, '0');
  const ss = String(seconds % 60).padStart(2, '0');
  const isConnected = status === 'connected';
  const isLoading = status === 'connecting' || status === 'idle';

  if (error) {
    return (
      <div className="flex h-[480px] flex-col items-center justify-between px-6 py-8 text-center">
        <div />
        <p className="text-sm text-gray-500">{t('global.something.went.wrong')}</p>
        <button
          type="button"
          onClick={onClose}
          className="flex size-14 items-center justify-center rounded-full bg-red-500 shadow-lg transition hover:bg-red-600 active:scale-95"
          aria-label="Close"
        >
          <PhoneXMarkIcon className="size-6 text-white" />
        </button>
      </div>
    );
  }

  return (
    <div className="flex h-[480px] flex-col items-center justify-between px-6 py-8 text-center">
      {isLoading ? (
        <>
          <div />
          <div className="flex flex-1 flex-col items-center justify-center space-y-4">
            <div className="size-8 animate-spin rounded-full border-2 border-neutral-300 border-t-secondary" />
            <p className="text-sm text-neutral-500">{t('audio-interview.connecting')}</p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="flex size-14 items-center justify-center rounded-full bg-red-500 shadow-lg transition hover:bg-red-600 active:scale-95"
            aria-label="End call"
          >
            <PhoneXMarkIcon className="size-6 text-white" />
          </button>
        </>
      ) : (
        <>
          <div className="text-sm tracking-widest text-neutral-500">
            {mm}:{ss}
          </div>
          <div className="flex flex-1 items-center justify-center">
            <PulsatingBestieAvatar isConnected={isConnected} />
          </div>
          <button
            type="button"
            onClick={onClose}
            className="flex size-14 items-center justify-center rounded-full bg-red-500 shadow-lg transition hover:bg-red-600 active:scale-95"
            aria-label="End call"
          >
            <PhoneXMarkIcon className="size-6 text-white" />
          </button>
        </>
      )}
    </div>
  );
}
